/**
 * Data layer for the `/profile` route (web/docs/data-layer.md). Mirrors the
 * mobile real client for the identity + stats surface
 * (`app/src/data/api-real.ts` — shapes only, nothing imported):
 *
 * - `GET /v1/profile` → the signed-in user's own profile. A confirmed
 *   `404 needs_setup` is answered with the idempotent
 *   `POST /v1/profile/bootstrap` (the app calls it after sign-in/session
 *   restore, docs/api-schema.md §POST /v1/profile/bootstrap), so the card
 *   always has an identity. Any other failure throws → error state.
 * - `GET /v1/stats` → server-computed viewing analytics; a `404` degrades to
 *   zeroed stats exactly like the app's `getStats`.
 * - `GET /v1/community/comments/mine` → the user's comment history (roots and
 *   replies, newest-first, with lean title cards); a `404` degrades to empty
 *   exactly like the app's `getMyComments`.
 * - `DELETE /v1/account` → starts permanent deletion (202); the caller signs
 *   out and returns to `/login`.
 */

import { useRef } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '@/api/http';
import { translate } from '@/i18n/locale';

import type {
  ApiAvailability,
  ApiNotificationPreferences,
  ApiOwnProfile,
  ApiStats,
  ApiUserCommentsPage,
} from '@shared/api-types';
import { mapCommentList, mapOwnProfile, mapStats } from '@shared/mappers';
import type { NotificationPreferences, OwnProfile, Socials, Stats, UserCommentsPage } from '@shared/types';

export const MY_PROFILE_QUERY_KEY = ['myProfile'] as const;
export const MY_STATS_QUERY_KEY = ['myStats'] as const;
export const MY_COMMENTS_QUERY_KEY = ['myComments'] as const;

/** Rows per page for the profile "Your comments" section. */
const MY_COMMENTS_PER_PAGE = 5;

export const EMPTY_STATS: Stats = {
  generatedAt: '',
  totals: {
    titlesTracked: 0,
    showsWatched: 0,
    moviesWatched: 0,
    uniqueMovies: 0,
    episodesWatched: 0,
    minutesWatched: 0,
    completedTitles: 0,
    rewatches: 0,
    currentStreakDays: 0,
    longestStreakDays: 0,
    activeDays: 0,
  },
  genres: [],
  topTitles: [],
  activity: { startDate: '', endDate: '', days: [] },
  dataQuality: { runtimeEstimated: false, undatedViewings: 0 },
};

/** `GET /v1/profile`, bootstrapping the profile when the user has none yet. */
export async function fetchMyProfile(): Promise<OwnProfile> {
  try {
    const profile = await apiGet<ApiOwnProfile>('/v1/profile');
    return mapOwnProfile(profile);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404 && err.body?.needs_setup === true) {
      const created = await apiPost<ApiOwnProfile>('/v1/profile/bootstrap');
      return mapOwnProfile(created);
    }
    throw err;
  }
}

/** `GET /v1/stats`; an unshipped endpoint degrades to zeroed stats. */
export async function fetchMyStats(): Promise<Stats> {
  try {
    const stats = await apiGet<ApiStats>('/v1/stats');
    return mapStats(stats);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return EMPTY_STATS;
    throw err;
  }
}

/**
 * One page of `GET /v1/community/comments/mine` (docs/api-schema.md §Community).
 * Mirrors the app's `getMyComments` wire shape (`ApiUserCommentsPage` →
 * `UserCommentsPage`); an unshipped endpoint degrades to an empty page.
 */
export async function fetchMyCommentsPage({
  cursor,
}: {
  cursor?: string;
} = {}): Promise<UserCommentsPage> {
  try {
    const page = await apiGet<ApiUserCommentsPage>('/v1/community/comments/mine', {
      query: { per_page: MY_COMMENTS_PER_PAGE, cursor },
    });
    return {
      username: page.username,
      publicUserId: page.public_user_id,
      comments: mapCommentList(page.comments),
      cursor: page.cursor ?? undefined,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { username: '', publicUserId: '', comments: [], cursor: undefined };
    }
    throw err;
  }
}

/** Start permanent account deletion (`202 { deleting: true }`). */
export function deleteAccount(): Promise<{ deleting?: boolean }> {
  return apiDelete<{ deleting?: boolean }>('/v1/account');
}

// --- profile editing ---------------------------------------------------------

/** `PUT /v1/profile` field limits (docs/api-schema.md §PUT /v1/profile). */
export const NAME_MAX = 40;
export const BIO_MAX = 200;
export const USERNAME_PATTERN = /^[a-z0-9_]{3,20}$/;

/** Avatar upload contract (backend/src/routes/social.py §avatar). */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
export const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export interface UpdateProfileFields {
  display_name?: string;
  bio?: string;
  username?: string;
  /** Replaces the stored map; `{}` clears it (see `socials.tsx`). */
  socials?: Socials;
}

/** `PUT /v1/profile` — partial update, returns the full updated profile. */
export function updateProfile(fields: UpdateProfileFields): Promise<OwnProfile> {
  return apiPut<ApiOwnProfile>('/v1/profile', fields).then(mapOwnProfile);
}

/**
 * `PUT /v1/profile` for the edit-profile screen. On success the `['myProfile']`
 * cache is invalidated so the card and the editor re-read the server truth.
 * Callers surface per-field errors inline (errors are announced — ui-ux.md §8).
 */
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
    },
  });
}

export interface UsernameAvailability {
  available: boolean;
  reason: 'taken' | 'reserved' | 'cooldown' | null;
}

/** `GET /v1/users/available?username=` — advisory (the claim stays authoritative). */
export async function fetchUsernameAvailability(
  username: string,
): Promise<UsernameAvailability> {
  const r = await apiGet<ApiAvailability>('/v1/users/available', {
    query: { username },
  });
  const reason =
    r.reason === 'taken' || r.reason === 'reserved' || r.reason === 'cooldown'
      ? r.reason
      : null;
  return { available: r.available, reason };
}

/**
 * Debounced advisory availability check for a candidate username. The
 * component owns the debounce; this hook only queries when `enabled` (the
 * value is a valid, non-unchanged username and no rename cooldown is active).
 */
export function useCheckUsername(username: string, opts: { enabled: boolean }) {
  return useQuery({
    queryKey: ['usernameAvailability', username],
    queryFn: () => fetchUsernameAvailability(username),
    enabled: opts.enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export interface AvatarUploadFile {
  blob: Blob;
  contentType: string;
}

/**
 * The three-step avatar flow (mirrors the app's `getAvatarUploadUrl` →
 * presigned S3 PUT → `confirmAvatar`):
 * 1. `POST /v1/profile/avatar/upload { content_type }` → `{ upload_url, key }`
 * 2. `PUT uploadUrl` with the raw bytes, direct to S3 (no auth header)
 * 3. `PUT /v1/profile/avatar { avatar_key }` → confirms + swaps in the object
 */
export async function uploadAvatar(file: AvatarUploadFile): Promise<string> {
  const r = await apiPost<{ upload_url: string; key: string }>(
    '/v1/profile/avatar/upload',
    { content_type: file.contentType },
  );
  const put = await fetch(r.upload_url, {
    method: 'PUT',
    body: file.blob,
    headers: { 'Content-Type': file.contentType },
  });
  if (!put.ok) {
    throw new ApiError(put.status, `Avatar upload failed (${put.status})`);
  }
  await apiPut<{ avatar_key: string }>('/v1/profile/avatar', {
    avatar_key: r.key,
  });
  return r.key;
}

/** The avatar flow above; on success the `['myProfile']` cache re-reads. */
export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadAvatar,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
    },
  });
}

/**
 * The signed-in user's card. Data comes from the query cache — PageTransition
 * re-serves cached route elements, so the screen must not rely on a fresh
 * mount (web/docs/data-layer.md).
 */
export function useMyProfile() {
  return useQuery({ queryKey: MY_PROFILE_QUERY_KEY, queryFn: fetchMyProfile });
}

/** Server-computed viewing analytics for the signed-in user. */
export function useMyStats() {
  return useQuery({ queryKey: MY_STATS_QUERY_KEY, queryFn: fetchMyStats });
}

/**
 * The signed-in user's comment history, newest-first. Infinite so "Your
 * comments" starts with one page and loads more inline via the opaque cursor.
 */
export function useMyComments() {
  return useInfiniteQuery({
    queryKey: MY_COMMENTS_QUERY_KEY,
    queryFn: ({ pageParam }) => fetchMyCommentsPage({ cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.cursor,
  });
}

/* --- notification preferences -------------------------------------------- */

export const NOTIFICATION_PREFS_QUERY_KEY = ['notificationPreferences'] as const;

/** Conservative server defaults (docs/api-schema.md §GET /v1/notifications/preferences). */
const DEFAULT_NOTIFICATION_PREFS: NotificationPreferences = {
  pushEnabled: false,
  commentMentions: false,
  commentReplies: false,
  calendarReminders: false,
  updatedAt: null,
};

/** Wire `ApiNotificationPreferences` → domain (same mapping as the app's real client). */
function mapNotificationPreferences(r: ApiNotificationPreferences): NotificationPreferences {
  return {
    pushEnabled: r.push_enabled,
    commentMentions: r.comment_mentions,
    commentReplies: r.comment_replies,
    calendarReminders: r.calendar_reminders ?? false,
    updatedAt: r.updated_at,
  };
}

/**
 * `GET /v1/notifications/preferences`. Browser push and device-local calendar
 * reminders are out of scope on web; an unshipped endpoint degrades to the
 * conservative defaults so the toggles simply read off.
 */
export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  try {
    const r = await apiGet<ApiNotificationPreferences>('/v1/notifications/preferences');
    return mapNotificationPreferences(r);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return DEFAULT_NOTIFICATION_PREFS;
    throw err;
  }
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: NOTIFICATION_PREFS_QUERY_KEY,
    queryFn: fetchNotificationPreferences,
  });
}

export interface UpdateNotificationPrefsVars {
  commentMentions: boolean;
  commentReplies: boolean;
}

/**
 * `PUT /v1/notifications/preferences` — a full replace, optimistically. The
 * web surface only owns the two comment toggles, so the PUT echoes the
 * server's current `push_enabled` / `calendar_reminders` unchanged (read from
 * the cached GET response) and only changes the two toggles.
 */
export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  // The ref lets the toast's Retry action re-enter the same mutation without
  // closing over a stale `mutate` (same pattern as notif/api.ts).
  const mutateRef = useRef<(vars: UpdateNotificationPrefsVars) => void>(() => {});
  // The mutationFn parameter is typed explicitly — contextual inference
  // collapses to `void` otherwise (see show/api.ts note).
  const mutation = useMutation<
    NotificationPreferences,
    Error,
    UpdateNotificationPrefsVars,
    { previous?: NotificationPreferences }
  >({
    mutationFn: async (vars: UpdateNotificationPrefsVars) => {
      const current = qc.getQueryData<NotificationPreferences>(NOTIFICATION_PREFS_QUERY_KEY);
      const r = await apiPut<ApiNotificationPreferences>('/v1/notifications/preferences', {
        push_enabled: current?.pushEnabled ?? false,
        comment_mentions: vars.commentMentions,
        comment_replies: vars.commentReplies,
        calendar_reminders: current?.calendarReminders ?? false,
      });
      return mapNotificationPreferences(r);
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: NOTIFICATION_PREFS_QUERY_KEY });
      const previous = qc.getQueryData<NotificationPreferences>(NOTIFICATION_PREFS_QUERY_KEY);
      qc.setQueryData<NotificationPreferences>(NOTIFICATION_PREFS_QUERY_KEY, (old) =>
        old ? { ...old, commentMentions: vars.commentMentions, commentReplies: vars.commentReplies } : old,
      );
      return { previous };
    },
    onSuccess: (data) => {
      qc.setQueryData(NOTIFICATION_PREFS_QUERY_KEY, data);
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(NOTIFICATION_PREFS_QUERY_KEY, ctx.previous);
      }
      toast.error(translate('notif.prefs.saveError'), {
        action: { label: translate('common.retry'), onClick: () => mutateRef.current(vars) },
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATION_PREFS_QUERY_KEY });
    },
  });
  mutateRef.current = mutation.mutate;
  return mutation;
}

/* --- import / export ("Your data") ---------------------------------------- */

export interface ExportJob {
  exportId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  format: string;
  counts?: Record<string, number>;
  byteSize?: number;
  downloadUrl?: string;
  expiresAt?: number;
  createdAt?: string;
  updatedAt?: string;
  error?: string;
}

/**
 * The source a user claims their import file came from. `auto` means "detect it".
 * Values match the backend's `source` body field on
 * `POST /v1/account/import/{id}/start` (docs/api-schema.md §Data import).
 */
export type ImportSource =
  | 'auto'
  | 'screenatlas'
  | 'generic'
  | 'tvtime'
  | 'trakt'
  | 'letterboxd'
  | 'simkl';

/** What the worker sniffed from the uploaded bytes (wire `detected`). */
export interface ImportDetected {
  format: string;
  files: string[];
  hintMismatch: boolean;
}

export interface ImportJob {
  importId: string;
  status: 'awaiting_upload' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
  sourceFormat?: string;
  sourceHint?: string;
  detected?: ImportDetected | null;
  counts?: Record<string, number>;
  reportUrl?: string;
  duplicateOf?: string;
  error?: string;
}

export interface ApiExportJob {
  export_id: string;
  status: ExportJob['status'];
  format: string;
  counts?: Record<string, number>;
  byte_size?: number;
  download_url?: string;
  expires_at?: number;
  created_at?: string;
  updated_at?: string;
  error?: string;
}

interface ApiImportDetected {
  format: string;
  files: string[];
  hint_mismatch: boolean;
}

interface ApiImportJob {
  import_id: string;
  status: ImportJob['status'];
  source_format?: string;
  source_hint?: string;
  detected?: ApiImportDetected | null;
  counts?: Record<string, number>;
  report_url?: string;
  duplicate_of?: string;
  error?: string;
}

function mapExport(job: ApiExportJob): ExportJob {
  return {
    exportId: job.export_id,
    status: job.status,
    format: job.format,
    counts: job.counts,
    byteSize: job.byte_size,
    downloadUrl: job.download_url,
    expiresAt: job.expires_at,
    createdAt: job.created_at,
    updatedAt: job.updated_at,
    error: job.error,
  };
}

export function mapImport(job: ApiImportJob): ImportJob {
  return {
    importId: job.import_id,
    status: job.status,
    sourceFormat: job.source_format,
    sourceHint: job.source_hint,
    detected: job.detected
      ? {
          format: job.detected.format,
          files: job.detected.files,
          hintMismatch: job.detected.hint_mismatch,
        }
      : null,
    counts: job.counts,
    reportUrl: job.report_url,
    duplicateOf: job.duplicate_of,
    error: job.error,
  };
}

/** `POST /v1/account/export` — P1 ships native JSON only (CSV is a later phase). */
export function createExport(): Promise<ExportJob> {
  return apiPost<ApiExportJob>('/v1/account/export', { format: 'json' }).then(mapExport);
}

/** `GET /v1/account/export/{id}` — poll for a download link. */
export function getExport(exportId: string): Promise<ExportJob> {
  return apiGet<ApiExportJob>(`/v1/account/export/${exportId}`).then(mapExport);
}

/** `GET /v1/account/exports` — the last 10 exports, newest first. */
export function listExports(): Promise<ExportJob[]> {
  return apiGet<{ exports: ApiExportJob[] }>('/v1/account/exports').then((r) =>
    r.exports.map(mapExport),
  );
}

/** `GET /v1/account/import/{id}` — poll for the results panel. */
export function getImport(importId: string): Promise<ImportJob> {
  return apiGet<ApiImportJob>(`/v1/account/import/${importId}`).then(mapImport);
}

/** `DELETE /v1/account/import/{id}` — cancel while queued/awaiting upload. */
export function cancelImport(importId: string): Promise<void> {
  return apiDelete<Record<string, unknown>>(`/v1/account/import/${importId}`).then(() => undefined);
}

/** `GET /v1/account/imports` — the last 20 imports, newest first. */
export function listImports(): Promise<ImportJob[]> {
  return apiGet<{ imports: ApiImportJob[] }>('/v1/account/imports').then((r) =>
    r.imports.map(mapImport),
  );
}

/** `POST /v1/account/import/{id}/start` — enqueue (idempotent) the uploaded file. */
export function startImport(importId: string, source?: ImportSource): Promise<void> {
  return apiPost<{ status: string }>(`/v1/account/import/${importId}/start`, {
    source,
  }).then(() => undefined);
}

/**
 * Retry `startImport` a few times for a transient blip while the tab stays
 * open (does not help the tab-closed case — that's the resume flow's job).
 */
const START_RETRY_DELAYS_MS = [500, 1500];

async function startImportWithRetry(importId: string, source?: ImportSource): Promise<void> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= START_RETRY_DELAYS_MS.length; attempt++) {
    try {
      await startImport(importId, source);
      return;
    } catch (err) {
      lastError = err;
      if (attempt < START_RETRY_DELAYS_MS.length) {
        await new Promise((resolve) => setTimeout(resolve, START_RETRY_DELAYS_MS[attempt]));
      }
    }
  }
  throw lastError;
}

/**
 * The content type we declare when reserving an upload slot. The backend
 * whitelists `application/json`, `text/csv`, `text/plain`, `application/zip`,
 * `application/x-zip-compressed`, and `application/octet-stream`; `file.type`
 * is preferred, falling back to the extension when the browser reports an
 * empty MIME (common for `.zip` on some platforms).
 */
const CONTENT_TYPE_BY_EXT: Record<string, string> = {
  '.zip': 'application/zip',
  '.csv': 'text/csv',
  '.json': 'application/json',
  '.txt': 'text/plain',
};

export function contentTypeFor(file: Pick<File, 'type' | 'name'>): string {
  if (file.type) return file.type;
  const name = file.name.toLowerCase();
  for (const [ext, mime] of Object.entries(CONTENT_TYPE_BY_EXT)) {
    if (name.endsWith(ext)) return mime;
  }
  return 'application/octet-stream';
}

/**
 * The three-step import flow: presigned POST upload (direct to S3, no auth
 * header), then `POST /v1/account/import/{id}/start` (with a small retry).
 * Returns the `import_id` to poll. `onProgress` reports the S3 upload
 * fraction (0–1) as the request body streams — `fetch` cannot observe that,
 * so the raw S3 hop uses `XMLHttpRequest` (everything else stays on the
 * shared client). `source` is the user's claimed origin, sent as the start
 * request's `source` body field.
 */
export async function importUpload(
  file: File,
  opts: { source?: ImportSource; onProgress?: (fraction: number) => void } = {},
): Promise<string> {
  const { source, onProgress } = opts;
  const r = await apiPost<{
    import_id: string;
    upload_url: string;
    fields: Record<string, string>;
  }>('/v1/account/import/upload', {
    content_type: contentTypeFor(file),
    content_length: file.size,
  });

  const form = new FormData();
  for (const [key, value] of Object.entries(r.fields)) form.append(key, value);
  form.append('file', file);
  await uploadToS3(r.upload_url, form, onProgress);

  await startImportWithRetry(r.import_id, source);
  return r.import_id;
}

/** Presigned S3 POST via `XMLHttpRequest` so upload progress is observable. */
function uploadToS3(
  url: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(event.loaded / event.total);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new ApiError(xhr.status, `Upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, 'Upload failed (network error)'));
    xhr.onabort = () => reject(new ApiError(0, 'Upload aborted'));

    xhr.send(form);
  });
}

const TERMINAL_EXPORT = new Set(['completed', 'failed']);
const TERMINAL_IMPORT = new Set(['completed', 'failed', 'cancelled']);

/** Poll an export job every 3 s, stopping once it reaches a terminal status. */
export function usePollExport(exportId: string | null) {
  return useQuery({
    queryKey: ['exportJob', exportId],
    queryFn: () => getExport(exportId as string),
    enabled: exportId !== null,
    refetchInterval: (query) =>
      query.state.data && TERMINAL_EXPORT.has(query.state.data.status) ? false : 3000,
  });
}

/** Poll an import job every 3 s, stopping once it reaches a terminal status. */
export function usePollImport(importId: string | null) {
  return useQuery({
    queryKey: ['importJob', importId],
    queryFn: () => getImport(importId as string),
    enabled: importId !== null,
    refetchInterval: (query) =>
      query.state.data && TERMINAL_IMPORT.has(query.state.data.status) ? false : 3000,
  });
}

export const MY_IMPORTS_QUERY_KEY = ['myImports'] as const;

/** The signed-in user's recent imports (last 20, newest first). No polling. */
export function useMyImports() {
  return useQuery({ queryKey: MY_IMPORTS_QUERY_KEY, queryFn: listImports });
}

export const MY_EXPORTS_QUERY_KEY = ['myExports'] as const;

/** The signed-in user's recent exports (last 10, newest first). No polling. */
export function useMyExports() {
  return useQuery({
    queryKey: MY_EXPORTS_QUERY_KEY,
    queryFn: listExports,
    staleTime: 60_000,
  });
}

/* --- unmatched report ------------------------------------------------------ */

/** One grouped entry in the unmatched report (wire `unmatched[]`). */
export interface ImportReportEntry {
  title: string;
  mediaType: 'show' | 'movie';
  year: number | null;
  externalIds: Record<string, unknown> | null;
  reason: string;
  count: number;
}

export interface ImportReport {
  unmatched: ImportReportEntry[];
  skipped: { file: string; reason: string }[];
  detected: ImportDetected | null;
}

interface ApiImportReportEntry {
  title: string;
  media_type: 'show' | 'movie';
  year: number | null;
  external_ids: Record<string, unknown> | null;
  reason: string;
  count: number;
}

interface ApiImportReport {
  unmatched: ApiImportReportEntry[];
  skipped: { file: string; reason: string }[];
  detected: ApiImportDetected | null;
}

function mapImportReport(r: ApiImportReport): ImportReport {
  return {
    unmatched: r.unmatched.map((e) => ({
      title: e.title,
      mediaType: e.media_type,
      year: e.year,
      externalIds: e.external_ids,
      reason: e.reason,
      count: e.count,
    })),
    skipped: r.skipped,
    detected: r.detected
      ? {
          format: r.detected.format,
          files: r.detected.files,
          hintMismatch: r.detected.hint_mismatch,
        }
      : null,
  };
}

/**
 * Fetch the unmatched report. `reportUrl` is a presigned S3 GET (no bearer),
 * so this is a plain `fetch` off the shared client.
 */
export async function fetchImportReport(reportUrl: string): Promise<ImportReport> {
  const res = await fetch(reportUrl);
  if (!res.ok) throw new ApiError(res.status, `Report fetch failed (${res.status})`);
  return mapImportReport((await res.json()) as ApiImportReport);
}

/** Best-effort fetch of the unmatched report for inline rendering. */
export function useImportReport(reportUrl: string | undefined) {
  return useQuery({
    queryKey: ['importReport', reportUrl],
    queryFn: () => fetchImportReport(reportUrl as string),
    enabled: reportUrl !== undefined,
    retry: false,
    staleTime: 60_000,
  });
}

interface MobileConfigFeature {
  enabled?: boolean;
  platforms?: string[];
  rollout_percentage?: number;
}

interface MobileConfig {
  features?: Record<string, MobileConfigFeature>;
}

export type ImportExportGate = 'on' | 'off' | 'unavailable';

/**
 * Feature gate for the "Your data" card, read from the public
 * `GET /config/mobile` document (remotely killable). Missing flag → off;
 * a failing/404 config endpoint → unavailable (a quiet "not available" card).
 */
export async function fetchImportExportGate(): Promise<ImportExportGate> {
  try {
    const cfg = await apiGet<MobileConfig>('/config/mobile', { auth: false });
    const feature = cfg.features?.import_export_v1;
    return feature?.enabled ? 'on' : 'off';
  } catch {
    return 'unavailable';
  }
}

export function useImportExportGate() {
  return useQuery({ queryKey: ['importExportGate'], queryFn: fetchImportExportGate });
}
